import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCompoundResourceApi } from "../../api/ResourceAPI";
import { EnhancedRichTextEditor } from "../../shared-components/EnhancedRichTextEditor";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { usePermissionValidator } from "../../api/UserPermissionValidators";
import { useCreateReportNote } from "../../api/ReportsApi";
import { HARDCODED_ACTOR_UUID } from "../../api/helpers";
const AUTOSAVE_DEBOUNCE_MS = 2000;
const INVESTIGATOR_NOTES_EDITOR_CLASS = "[&_.ql-editor]:text-[#353744]";

type Props = {
  reportId: string;
  onStatusChange?: (status: string) => void;
  showStatusInline?: boolean;
};
interface Notes {
  external_id?: string;
  body: string;
}

const HL_OPEN = '<span style="background-color: rgb(255, 255, 204);">';
const HL_CLOSE = "</span>";
const hl = (inner: string) => `${HL_OPEN}${inner}${HL_CLOSE}`;

const affiliationBooleanHighlighted = `(${Array(46)
  .fill(hl("&quot;Affiliation&quot;"))
  .join(" OR ")})`;

const nameAroundPattern = `(${hl("First")} OR ${hl("Alias")} AROUND(2) ${hl(
  "Last"
)} OR ${hl("Alias")})`;

const initialInvestigatorNotes = `<p class="ql-align-center"><br></p>
<p><span style="background-color: rgb(255, 255, 204);">List the exact search parameters used on
        each site </span><strong style="background-color: rgb(255, 255, 204);">and</strong><span
        style="background-color: rgb(255, 255, 204);"> the results in the provided space below.
        This document will be used for audits or in the event that the client opens a dispute about the press
        check.&nbsp;</span></p>
<p><br></p>
<p><strong>Enhanced Analyst Name:&nbsp;&nbsp;</strong></p>
<p><strong>Investigations Associate Name:&nbsp;&nbsp;</strong></p>
<p><br></p>
<p><strong>Candidate Name:&nbsp;&nbsp;</strong></p>
<p>AKA(s):&nbsp;&nbsp;</p>
<p>Locations:</p>
<p><br></p>
<p><strong>Name Booleans:</strong></p>
<ul>
    <li>${hl("&quot;First Last&quot;")} OR ${hl("&quot;Alias Alias&quot;")}</li>
    <li>${nameAroundPattern}</li>
</ul>
<p><br></p>
<p><strong>Affiliation Boolean:</strong></p>
<p>${affiliationBooleanHighlighted}</p>
<p><br></p>
<p><strong>Adverse Google Findings for Investigator Review:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>Google Search Templates:</strong></p>
<ul>
    <li>${hl("First Last")}</li>
    <li>${hl("&quot;First Last&quot;")} OR ${hl("&quot;Alias Alias&quot;")}</li>
    <li>${hl("&quot;First Last&quot;")} OR ${hl(
  "&quot;Alias Alias&quot;"
)} AND (accused OR alleg OR breach OR conflict OR controvers OR discriminat OR dispute OR fired OR fraud OR harass OR malpractice OR negligent OR ousted OR racist OR scam OR scandal OR terminat OR violate)</li>
</ul>
<p style="border-top: 1px solid rgb(221, 221, 221); margin: 0.5rem 0;"><br></p>
<ul>
    <li>${hl("&quot;First Last&quot;")} OR ${hl(
  "&quot;Alias Alias&quot;"
)} AND (${hl("&quot;Affiliation&quot;")})</li>
    <li>${nameAroundPattern} AND (${hl(
  "&quot;Affiliation&quot;"
)}) AND (backlash OR boycott OR petition OR no confidence OR resign OR suspen)</li>
    <li>${nameAroundPattern} AND (${hl(
  "&quot;Affiliation&quot;"
)}) AND (accused OR alleg OR breach OR conflict OR controvers OR discriminat OR dispute OR fired OR fraud)</li>
    <li>${nameAroundPattern} AND (${hl(
  "&quot;Affiliation&quot;"
)}) AND (harass OR malpractice OR negligent OR ousted OR racist OR scam OR scandal OR terminat OR violate)</li>
    <li>${nameAroundPattern} AND (&quot;arrest&quot; OR &quot;charged&quot; OR &quot;convict&quot; OR &quot;corrupt&quot; OR &quot;indict&quot; OR &quot;racketeer&quot; OR &quot;traitor&quot; OR &quot;bribe&quot; OR &quot;mugshot&quot; OR &quot;DUI&quot; OR &quot;DWI&quot;)</li>
</ul>
<p><br></p>
<p><strong>Notes:</strong> Include <strong><u>all</u></strong> relevant affiliations in these searches.</p>
<p>If Google&apos;s 32-word limit is hit, break the Boolean up as appropriate.</p>
<p>The limit does not count punctuation or Boolean operators such as AROUND(2), AND, or OR.</p>
<p>Limit the Google scope as follows: 5 pages for each individual search string.</p>
<p><br></p>
<p>Affiliations = Address, Education, Employers, Boards, &amp; Volunteering</p>
<p>Do not include certifications or training.</p>
<p>For academic and medical professionals, do not include research or publications.</p>
<p><br></p>
<p><strong>Plus Notes (if applicable):</strong></p>
<ul>
    <li><br></li>
</ul>
<p style="border-top: 1px solid rgb(221, 221, 221); margin: 0.75rem 0;"><br></p>
<p><strong>Investigator Notes</strong> (if applicable - ex: locations, Quality Chat instructions, etc.):</p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>Lexis Cases:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>Lexis News:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>Nexis:</strong></p>
<ul>
    <li><br></li>
</ul>
<p style="border-top: 1px dotted rgb(200, 200, 200); margin: 0.5rem 0;"><br></p>
<p><strong>Courtlink:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>Pacer:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><em>If applicable:</em></p>
<p><br></p>
<p><strong>Diligence:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>NCAA:</strong></p>
<ul>
    <li><br></li>
</ul>
<p><br></p>
<p><strong>NSOPW:</strong></p>
<p><br></p>`;

const Reports360NotesAutosave = ({
  reportId,
  onStatusChange,
  showStatusInline = true,
}: Props) => {
  const [investigatorNotes, setInvestigatorNotes] = useState<string>(
    initialInvestigatorNotes
  );
  const { canEdit360Reports } = usePermissionValidator();
  const [noteId, setNoteId] = useState<string>("");
  const [lastSaved, setLastSaved] = useState<string>("");
  const [prevNotes, setPrevNotes] = useState<string>("");
  const [serverSynced, setServerSynced] = useState(false);
  const investigatorNotesRef = useRef(investigatorNotes);
  investigatorNotesRef.current = investigatorNotes;
  const prevNotesRef = useRef(prevNotes);
  prevNotesRef.current = prevNotes;

  const { mutate: createReportNote } = useCreateReportNote();
  const {
    data: loadedNotes,
    isLoading,
    create,
    update,
  } = useCompoundResourceApi<Notes>(
    `itemdal/reports/${reportId}/notes`,
    `itemdal/notes`
  );

  const saveInvestigatorNotes = useCallback(
    (value: string) => {
      if (prevNotes === value) {
        return;
      }
      setPrevNotes(value);

      if (noteId) {
        update.mutate({ id: noteId, data: { body: value } });
      } else {
        create.mutate({ body: value });
      }
      setLastSaved(new Date().toLocaleString());
    },
    [noteId, prevNotes, create, update]
  );

  const saveInvestigatorNotesRef = useRef(saveInvestigatorNotes);
  saveInvestigatorNotesRef.current = saveInvestigatorNotes;

  useEffect(() => {
    setServerSynced(false);
  }, [reportId]);

  useEffect(() => {
    if (isLoading || loadedNotes === undefined) {
      return;
    }
    if (loadedNotes.length > 0) {
      if (!noteId) {
        const body = loadedNotes[0].body;
        setInvestigatorNotes(body);
        setPrevNotes(body);
      }
      if (loadedNotes[0].external_id) {
        setNoteId(loadedNotes[0].external_id);
      }
    } else {
      setPrevNotes(initialInvestigatorNotes);
    }
    setServerSynced(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedNotes, isLoading, noteId]);

  useEffect(() => {
    const handleUnload = (e: BeforeUnloadEvent) => {
      const v = investigatorNotesRef.current;
      const p = prevNotesRef.current;
      if (v !== p) {
        saveInvestigatorNotesRef.current(v);
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, []);

  useEffect(() => {
    if (
      !serverSynced ||
      isLoading ||
      !canEdit360Reports ||
      investigatorNotes === prevNotes
    ) {
      return;
    }
    const t = window.setTimeout(() => {
      saveInvestigatorNotes(investigatorNotes);
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [
    serverSynced,
    investigatorNotes,
    prevNotes,
    isLoading,
    canEdit360Reports,
    saveInvestigatorNotes,
  ]);

  const handleInvestigatorNotesBlur = (value: string) => {
    if (prevNotesRef.current !== value) {
      createReportNote({
        report_uuid: reportId,
        note: value,
        actor_uuid: HARDCODED_ACTOR_UUID, // TODO: real actor uuid
      });
    }
    setInvestigatorNotes(value);
    saveInvestigatorNotes(value);
  };

  const statusLabel = isLoading
    ? "Loading..."
    : lastSaved
    ? `Last Saved: ${lastSaved}`
    : "";

  useEffect(() => {
    if (!onStatusChange) return;
    onStatusChange(statusLabel);
  }, [onStatusChange, statusLabel]);

  return (
    <>
      {showStatusInline && (
        <div className="flex justify-end items-center pb-3 min-h-[1.5rem]">
          {statusLabel && !isLoading && (
            <div className="text-sm text-gray-500">{statusLabel}</div>
          )}
          {isLoading && (
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <LoadingSpinner />
              <span>Loading...</span>
            </div>
          )}
        </div>
      )}
      <EnhancedRichTextEditor
        name="investigator_notes"
        classes="flex flex-1"
        editorClassName={INVESTIGATOR_NOTES_EDITOR_CLASS}
        value={investigatorNotes}
        handleChange={(value) => setInvestigatorNotes(value)}
        handleBlur={(value) => handleInvestigatorNotesBlur(value)}
        readOnly={!canEdit360Reports}
        blockImagePaste
      />
    </>
  );
};

export default Reports360NotesAutosave;
